import React, { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { GripVertical, Lock, PinOff, ArrowLeftToLine, ArrowRightToLine, RotateCcw } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableRow({ col, checked, pin, onToggle, onPin }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const pinnable = col.pinnable !== false;
  const pinBtn = (side, Icon) => (
    <button type="button" onClick={() => onPin(col.key, pin === side ? null : side)} disabled={!pinnable}
      title={side === "left" ? "Pin left" : "Pin right"} data-testid={`column-pin-${side}-${col.key}`}
      className={`p-1 rounded transition-colors ${!pinnable ? "opacity-30 cursor-not-allowed" : pin === side ? "bg-indigo-100 text-indigo-700" : "text-slate-300 hover:text-slate-600 hover:bg-slate-100"}`}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style}
      className="flex items-center gap-2 py-2 px-1 -mx-1 rounded-md hover:bg-slate-50 transition-colors bg-white"
      data-testid={`column-row-${col.key}`}>
      <button type="button" className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"
        data-testid={`column-drag-${col.key}`} {...attributes} {...listeners}>
        <GripVertical className="w-4 h-4" />
      </button>
      {col.mandatory ? (
        <span className="inline-flex items-center justify-center w-9" title="Required column — always shown" data-testid={`column-mandatory-${col.key}`}>
          <Lock className="w-3.5 h-3.5 text-slate-400" />
        </span>
      ) : (
        <Switch checked={checked} onCheckedChange={() => onToggle(col.key)} data-testid={`column-toggle-${col.key}`} />
      )}
      <span className="text-sm text-slate-700 flex-1 truncate">{col.label}</span>
      <div className="flex items-center gap-0.5">
        {pinBtn("left", ArrowLeftToLine)}
        {pinBtn("right", ArrowRightToLine)}
      </div>
    </div>
  );
}

// Right-side "Table Settings" panel: visibility, drag-reorder, pin left/right, reset.
export default function ColumnSettings({ open, onOpenChange, columns, order: orderProp, visibleKeys, pinned: pinnedProp, onApply, onReset }) {
  const buildOrder = () => {
    const base = (orderProp && orderProp.length ? orderProp : visibleKeys) || [];
    const remaining = columns.map((c) => c.key).filter((k) => !base.includes(k));
    return [...base.filter((k) => columns.some((c) => c.key === k)), ...remaining];
  };
  const [order, setOrder] = useState(buildOrder);
  const [visible, setVisible] = useState(() => new Set(visibleKeys));
  const [pinLeft, setPinLeft] = useState(() => new Set(pinnedProp?.left || []));
  const [pinRight, setPinRight] = useState(() => new Set(pinnedProp?.right || []));

  useEffect(() => {
    if (open) {
      setOrder(buildOrder());
      setVisible(new Set(visibleKeys));
      setPinLeft(new Set(pinnedProp?.left || []));
      setPinRight(new Set(pinnedProp?.right || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visibleKeys, columns, orderProp, pinnedProp]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const colFor = (k) => columns.find((c) => c.key === k);
  const optional = columns.filter((c) => !c.mandatory);
  const allOn = optional.every((c) => visible.has(c.key));

  const toggleAll = () => setVisible(() => {
    const next = new Set(columns.filter((c) => c.mandatory).map((c) => c.key));
    if (!allOn) optional.forEach((c) => next.add(c.key));
    return next;
  });
  const toggle = (key) => setVisible((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const pinOf = (key) => (pinLeft.has(key) ? "left" : pinRight.has(key) ? "right" : null);
  const setPin = (key, side) => {
    setPinLeft((prev) => { const n = new Set(prev); n.delete(key); if (side === "left") n.add(key); return n; });
    setPinRight((prev) => { const n = new Set(prev); n.delete(key); if (side === "right") n.add(key); return n; });
    if (side) setVisible((prev) => new Set(prev).add(key)); // pinning implies visible
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setOrder((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
  };

  const apply = () => {
    const vis = new Set(visible);
    columns.forEach((c) => { if (c.mandatory) vis.add(c.key); });
    onApply({
      order,
      visible: order.filter((k) => vis.has(k)),
      pinned: { left: order.filter((k) => pinLeft.has(k)), right: order.filter((k) => pinRight.has(k)) },
    });
    onOpenChange(false);
  };
  const doReset = () => { onReset?.(); onOpenChange(false); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="table-settings-panel">
        <SheetHeader className="px-6 py-5 border-b border-slate-100">
          <SheetTitle className="text-xl font-bold text-slate-900">Table Settings</SheetTitle>
          <SheetDescription className="text-sm text-slate-500">
            Show/hide, drag to reorder, pin left/right, and resize by dragging a column edge.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Columns</p>
            <button onClick={doReset} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-indigo-600 transition-colors" data-testid="column-settings-reset">
              <RotateCcw className="w-3.5 h-3.5" /> Reset to default
            </button>
          </div>

          <label className="flex items-center gap-2 py-2 cursor-pointer">
            <span className="w-4" />
            <Switch checked={allOn} onCheckedChange={toggleAll} data-testid="column-toggle-select-all" />
            <span className="text-sm font-semibold text-slate-800">Select all optional</span>
          </label>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              <div className="mt-1 space-y-0.5">
                {order.map((k) => {
                  const col = colFor(k);
                  if (!col) return null;
                  return <SortableRow key={k} col={col} checked={visible.has(k) || !!col.mandatory} pin={pinOf(k)} onToggle={toggle} onPin={setPin} />;
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="column-settings-cancel">Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={apply} data-testid="column-settings-apply">Apply Changes</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
